import { NavView } from "@gravity/tools";
import { useUserState, useWebSocket } from "@gx1/gx-npm-lib";
import { Fragment, useContext, useEffect } from "react";
import EvaluationHeaderComponent from "./components/eval-header/eval-header.component";
import GlobalHeaderComponent from "./components/global-header/global-header.component";
import GlobalNavigationComponent from "./components/global-navigation/global-navigation.component";
import NotificationsV2Component from "./components/notifications-v2/notifications-v2.component";
import { WebSocketMessageEvent } from "./app.constants";
import { MainNavAppV2Context } from "./app-v2.context";
import FreeTrialHeaderComponent from "./components/free-trial-header/free-trial-header.component";

type AppV3Props = { view: NavView };

const AppV3Component = ({ view }: AppV3Props) => {
  const { setNotificationCount } = useContext(MainNavAppV2Context);
  const { freeTrialDaysRemaining, unreadNotificationCount, freeTrialRole } = useUserState();
  const [wsMessage] = useWebSocket<{ count: number; event: WebSocketMessageEvent; }, never>();

  useEffect(() => {
    window.eventBus.publish("USER_STATE_LOAD");
  }, []);

  useEffect(() => {
    void (async () => {
      try {
        const response = await window.fetch(`${window.gxApiConnection}/api/v3/user/analytics/intercom`, {
          credentials: "include",
          method: "GET",
        });
        if (!response.ok) {
          return;
        }
        const { data } = await response.json();
        if (Boolean(data.email) && typeof window.dataLayer === "object") {
          const payload = {
            USER_BROWSING_REASON: data.browsingReason || "",
            USER_EMAIL: data.email || "",
            USER_FULL_NAME: data.fullName || "",
            USER_HAS_CONTRIBUTOR_ROLE: Boolean(data.hasContributorRole),
            USER_HAS_NAMED_INIT: Boolean(data.hasNamedInitiative),
            USER_HAS_OWNER_ROLE: Boolean(data.hasOwnerRole),
            USER_HAS_SCORED_INIT: Boolean(data.hasScoredInitiative),
            USER_HAS_VENDOR_SURVEY_RESPONSE: Boolean(data.hasVendorSurveyResponse),
            USER_HAS_VIEWER_ROLE: Boolean(data.hasViewerRole),
            USER_HASH: data.userIdHash || "",
            USER_INTERCOM_HMAC: data.userHMAC || "",
            USER_LATEST_UNNAMED_INIT_CREATE_DATE: data.latestInitCreatedDate || "",
            USER_LATEST_UNNAMED_INIT_TECH_NAME: data.latestInitTemplateTechName || "",
          };
          window.dataLayer.push({ event: "user-analytics-intercom-hook", ...payload });
          window.dataLayer.push({ event: "everyone-ready", ...payload });
        }
        if (window.Intercom) {
          window.Intercom("boot");
        }
      } catch {}
    })();
  }, []);

  useEffect(() => {
    setNotificationCount(unreadNotificationCount || 0);
  }, [unreadNotificationCount, setNotificationCount]);

  useEffect(() => {
    if (!wsMessage || wsMessage.event !== WebSocketMessageEvent.UNREAD_NOTIFICATION_COUNT) {
      return;
    }
    setNotificationCount(wsMessage.count || 0);
  }, [wsMessage, setNotificationCount]);

  return (
    <Fragment>
      <GlobalHeaderComponent />
      <GlobalNavigationComponent view={view} />
      <FreeTrialHeaderComponent daysRemaining={freeTrialDaysRemaining} freeTrialRole={freeTrialRole} />
      {view === NavView.WORKFLOW && <EvaluationHeaderComponent freeTrialRole={freeTrialRole} />}
      <NotificationsV2Component />
    </Fragment>
  );
};

export default AppV3Component;
